import { useTypedCreateRecordDialog } from 'hooks/typedRecordDialogs/useTypedCreateRecordDialog';
import { useCreateKitRequestsMutation } from 'store/slices/clientV2/kitRequests';
import {
  createUpdateKitRequestFormConstants,
  createUpdateKitRequestValidationSchema,
  CreateKitRequestFormInputData,
} from 'views/kitting/components/constants';
import CreateUpdateKitRequestForm, {
  CreateUpdateKitRequestFormRef,
} from 'views/kitting/components/CreateUpdateKitRequestForm';
import { createUpdateKitRequestPreSubmit } from 'views/kitting/components/utils';
import { FormProvider } from 'react-hook-form';
import { KitRequestSchema, KitRequestPOSTSchema } from 'types/kitting';
import { useEffect, useMemo, useRef } from 'react';
import useGetCustomPropertyDefinitions from 'hooks/useGetCustomPropertyDefinitions';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import { ALL_RECORD_TYPE_IDS } from 'constants/recordTypes';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { useGetTeamQuery } from 'store/slices/apiV1/properties';
import { useSelector } from 'store';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { getEffectiveDefaultShippingPreferences } from 'utils/functions';

type CreateKitRequestDialogProps = {
  dialogOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: (newKitRequestId: KitRequestSchema['id']) => void;
  overrideDefaults?: Partial<CreateKitRequestFormInputData>;
  shouldUnregister?: boolean;
};

const CreateKitRequestDialog = ({
  dialogOpen,
  onClose,
  onCreateSuccess,
  overrideDefaults,
  shouldUnregister = false,
}: CreateKitRequestDialogProps) => {
  const formRef = useRef<CreateUpdateKitRequestFormRef>(null);
  const {
    customProperties,
    getDefaultValuesForCustomProperties,
    customPropertiesFieldName,
  } = useGetCustomPropertyDefinitions({
    model: CustomPropertyModel.KIT_REQUEST,
    visibilityContext: CustomPropertyVisibility.NEW_RECORD_DIALOG,
  });
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);

  const overrideTeamId =
    overrideDefaults?.[createUpdateKitRequestFormConstants.team.id];
  const { data: overrideTeam } = useGetTeamQuery(overrideTeamId ?? skipToken);
  const effectiveDefaults = useMemo(
    () => getEffectiveDefaultShippingPreferences({ org, team: overrideTeam }),
    [org, overrideTeam]
  );

  const formDefaults = useMemo(
    (): CreateKitRequestFormInputData => ({
      [createUpdateKitRequestFormConstants.name.id]: '',
      [createUpdateKitRequestFormConstants.printedNotes.id]: null,
      [createUpdateKitRequestFormConstants.sourceLocations.id]:
        effectiveDefaults.defaultCofactrWarehouseId
          ? [effectiveDefaults.defaultCofactrWarehouseId]
          : [],
      [createUpdateKitRequestFormConstants.productionRun.id]: null,
      [createUpdateKitRequestFormConstants.team.id]: null,
      [createUpdateKitRequestFormConstants.shipTo.id]: null,
      [createUpdateKitRequestFormConstants.shipMethod.id]: null,
      [createUpdateKitRequestFormConstants.shipPartial.id]: false,
      [customPropertiesFieldName]: getDefaultValuesForCustomProperties(),
    }),
    [
      customPropertiesFieldName,
      effectiveDefaults.defaultCofactrWarehouseId,
      getDefaultValuesForCustomProperties,
    ]
  );

  const { methods, BaseCreateRecordDialog, dialogProps } =
    useTypedCreateRecordDialog<
      typeof createUpdateKitRequestValidationSchema,
      KitRequestPOSTSchema,
      KitRequestSchema
    >({
      validationSchema: createUpdateKitRequestValidationSchema,
      defaultValues: formDefaults,
      useCreateMutation: useCreateKitRequestsMutation,
      recordName: 'Kit',
      preSubmit: createUpdateKitRequestPreSubmit,
      onSave: (kitRequestIds: string[]) =>
        onCreateSuccess?.(kitRequestIds?.[0]),
      recordType: ALL_RECORD_TYPE_IDS.KIT_REQUEST,
      allowAttachments: true,
    });

  useEffect(() => {
    if (dialogOpen) {
      methods.reset({ ...formDefaults, ...overrideDefaults });
    }
  }, [methods.reset, overrideDefaults, dialogOpen, formDefaults]);

  return (
    <BaseCreateRecordDialog
      open={dialogOpen}
      onClose={onClose}
      dialogProps={dialogProps}
      submitDisabled={formRef.current?.isFetching}
    >
      <FormProvider {...methods}>
        <CreateUpdateKitRequestForm
          ref={formRef}
          defaultValues={formDefaults}
          disableProgramField={
            !!overrideDefaults?.[
              createUpdateKitRequestFormConstants.productionRun.id
            ]
          }
          customProperties={customProperties}
          shouldUnregister={shouldUnregister}
        />
      </FormProvider>
    </BaseCreateRecordDialog>
  );
};

export default CreateKitRequestDialog;
